import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { API, useAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { toast } from "sonner";
import { Trophy, Brain, Flag, QrCode, Radio } from "lucide-react";
import QuizPlayer from "../components/game/QuizPlayer";
import Predictions from "../components/game/Predictions";
import Missions from "../components/game/Missions";
import Leaderboard from "../components/game/Leaderboard";

import LiveTiming from "../components/game/LiveTiming";
import FantaTeam from "../components/game/FantaTeam";
import ShareButton from "../components/share/ShareButton";
import CreditClaims from "../components/CreditClaims";
import RallyInfo from "../components/game/RallyInfo";
import { Users, Info, Share2 } from "lucide-react";
import { StatusPill } from "../components/RallyCard";
import { fmtRange } from "../lib/dates";

const TABS = [["INFO", Info], ["LIVE", Radio], ["QUIZ", Brain], ["FANTARALLY", Flag], ["SQUADRA", Users], ["MISSIONI", QrCode], ["CLASSIFICA", Trophy]];
const absUrl = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function PaddockGame() {
  const { id } = useParams();
  const { user } = useAuth(); const { c, img } = useContent();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState({ points: 0, rank: null, done: [], picks: [] });
  const [params] = useSearchParams();
  const initTab = (params.get("tab") || "").toUpperCase();
  const [tab, setTab] = useState(TABS.some(t => t[0] === initTab) ? initTab : "FANTARALLY");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/paddock/rallies/${id}`);
      setData(data);
      if (user) axios.get(`${API}/paddock/rallies/${id}/me`).then(r => setMe(r.data)).catch(() => {});
    } catch { toast.error("Rally non trovato"); nav("/paddock"); }
  };
  useEffect(() => { load(); }, [id, user]);

  const requireLogin = () => { if (!user) { toast.info("Accedi per giocare"); nav(`/login?next=/paddock/${id}`); return false; } return true; };

  if (!data) return <div className="min-h-screen grain-bg pt-24 text-center text-slate-400">Caricamento Paddock…</div>;
  const { rally } = data;
  const dates = fmtRange(rally.start_date, rally.end_date);
  const rallyCard = { kicker: "ENTRA NELL'OFFICIAL PADDOCK", lines: [rally.name, dates || rally.location || "", rally.location && dates ? rally.location : ""], cta: "SCANSIONA E GIOCA", rallyLogo: absUrl(rally.logo_url), sponsorLogos: (rally.sponsor_logos || []).map(s => absUrl(s.url)) };
  const rallyShare = { target: `/paddock/${id}`, title: `${rally.name} · Official Paddock`, description: `${dates ? dates + " · " : ""}Entra nel Paddock, gioca a FANTARALLY, quiz e missioni.`, rally_id: id };
  const nick = user?.nickname || user?.name?.split(" ")[0];

  return (
    <div className="min-h-screen grain-bg">
      <Navbar variant="pro" />
      <section className="pt-20 pb-6 max-w-7xl mx-auto px-5 lg:px-8">
        {rally.cover_image && (
          <div className="relative h-40 md:h-64 rounded-2xl overflow-hidden mb-6" data-testid="game-cover">
            <img src={absUrl(rally.cover_image)} alt={`Copertina ${rally.name}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#08090D] via-transparent to-transparent" />
          </div>
        )}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="pill-badge">OFFICIAL PADDOCK · PLAY RALLY</div>
              <StatusPill start={rally.start_date} end={rally.end_date} />
              {rally.demo && <span className="px-2 py-0.5 rounded-full font-mono-rl text-[10px] tracking-widest bg-black/60 border border-white/15 text-slate-300" data-testid="game-demo-badge">DEMO</span>}
            </div>
            <div className="flex items-center gap-4">
              {rally.logo_url && <img src={absUrl(rally.logo_url)} alt="" className="h-16 md:h-20 object-contain" data-testid="game-rally-logo" />}
              <h1 className="font-display font-black text-3xl md:text-5xl leading-none" data-testid="game-rally-name">{rally.name}</h1>
            </div>
            <div className="font-mono-rl text-sm text-slate-400 mt-2">{rally.location}{fmtRange(rally.start_date, rally.end_date) ? ` · ${fmtRange(rally.start_date, rally.end_date)}` : ""} · {data.entries.length} EQUIPAGGI · {data.stages.length} PS · {data.fans} FANS</div>
            <ShareButton kind="rally" card={rallyCard} share={rallyShare} caption={`Entra nell'Official Paddock del ${rally.name} su Rally Love: FANTARALLY, quiz e missioni. Ti aspetto: [link]`} className="mt-3 !py-1.5 !px-3 text-xs" label="CONDIVIDI EVENTO" testid="share-event-btn" />
          </div>
          <div className="rl-card rl-watermark px-6 py-4 flex items-center gap-5" data-testid="my-score-card">
            <img src="/logo-mark.png" alt="" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-xs font-mono-rl text-slate-400 tracking-widest">I TUOI PUNTI</div>
              <div className="font-display font-black text-3xl" data-testid="my-points">{me.points}</div>
            </div>
            <div>
              <div className="text-xs font-mono-rl text-slate-400 tracking-widest">POSIZIONE</div>
              <div className="font-display font-black text-3xl text-[#30D158]" data-testid="my-rank">{me.rank ? `P${me.rank}` : "—"}</div>
            </div>
            {user && <ShareButton kind="leaderboard" variant="solid" className="!py-2 !px-4 text-xs" label="CONDIVIDI" testid="share-card-btn"
              card={{ kicker: rally.name, lines: [me.rank ? `Sono ${me.rank}° nel FantaRally` : "Sto giocando il FantaRally", `${me.points} punti FantaRally`, "Riuscirai a superarmi?"], cta: "SCALA LA CLASSIFICA", nickname: nick, avatar: absUrl(user.avatar_url || user.picture), rallyLogo: absUrl(rally.logo_url), sponsorLogos: (rally.sponsor_logos || []).map(s => absUrl(s.url)) }}
              share={{ target: `/paddock/${id}?tab=CLASSIFICA`, title: `${nick} è ${me.rank ? `P${me.rank}` : "in gara"} nel FantaRally · ${rally.name}`, description: `${me.points} punti FantaRally. Riuscirai a superarlo? Entra nel Paddock.`, rally_id: id }}
              caption={`Sono ${me.rank ? me.rank + "°" : "in gara"} nel FantaRally del ${rally.name} con ${me.points} punti! Riesci a superarmi? Entra su Rally Love: [link]`} />}
          </div>
        </div>
        {img("sponsor.banner_image") && <a href={c("sponsor.banner_link") || undefined} target={c("sponsor.banner_link") ? "_blank" : undefined} rel="noreferrer" className="block mt-6 rl-card overflow-hidden" data-testid="sponsor-banner"><img src={img("sponsor.banner_image")} alt="Sponsor" className="w-full max-h-40 object-cover" /></a>}
        {rally.sponsor_logos?.length > 0 && (
          <div className="mt-6 rl-card px-5 py-3 flex flex-wrap items-center gap-6" data-testid="game-sponsor-strip">
            <span className="text-[10px] font-mono-rl text-slate-500 tracking-widest">OFFICIAL PARTNERS</span>
            {rally.sponsor_logos.map((sp, i) => { const src = absUrl(sp.url); const im = <img key={i} src={src} alt={sp.name} title={sp.name} className="h-10 max-w-[140px] object-contain" />; return sp.link ? <a key={i} href={sp.link} target="_blank" rel="noreferrer">{im}</a> : im; })}
          </div>
        )}
        {rally.demo && <p className="mt-4 text-[11px] text-slate-500" data-testid="game-demo-disclaimer">Evento, partecipanti, risultati e sponsor presenti in questa dimostrazione sono completamente fittizi. FANTARALLY utilizza esclusivamente punti virtuali.</p>}
        <CreditClaims rallyId={id} />
        <div className="flex flex-wrap gap-2 mt-8">
          {TABS.map(([t, Icon]) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`game-tab-${t.toLowerCase()}`}
              className={`px-4 py-2 rounded-full font-mono-rl text-xs uppercase tracking-widest transition flex items-center gap-2 ${tab === t ? "bg-[#FF1E27] text-white" : "border border-white/10 text-slate-300 hover:border-[#FF1E27]/40"}`}>
              <Icon className="w-3 h-3" /> {t}
            </button>
          ))}
        </div>
      </section>
      <section className="pb-20 max-w-7xl mx-auto px-5 lg:px-8">
        {tab === "INFO" && <RallyInfo rally={rally} stages={data.stages} />}
        {tab === "LIVE" && <LiveTiming rallyId={id} entries={data.entries} />}
        {tab === "SQUADRA" && <FantaTeam rallyId={id} requireLogin={requireLogin} onDone={load} user={user} />}
        {tab === "QUIZ" && <QuizPlayer quizzes={data.quizzes} stages={data.stages} done={me.done} requireLogin={requireLogin} onDone={load} shareCtx={{ rally, rallyId: id, nick }} />}
        {tab === "FANTARALLY" && <Predictions predictions={data.predictions} entries={data.entries} picks={me.picks} requireLogin={requireLogin} onDone={load} shareCtx={{ rally, rallyId: id, nick, rank: me.rank, points: me.points }} />}
        {tab === "MISSIONI" && <Missions missions={data.missions} done={me.done} requireLogin={requireLogin} onDone={load} />}
        {tab === "CLASSIFICA" && <Leaderboard rallyId={id} />}
      </section>
    </div>
  );
}
